import React, { useState } from 'react'
import { Form, Input, message, Modal, Typography } from 'antd'
import { ContentSteps } from './components/ContentSteps'
import { ContentStep } from './components/ContentStep'
import styles from './components/steps.module.css'
import modalStyles from './changePasswordModal.module.css'
import { EditOutlined } from '@ant-design/icons'
import MaskedInput from 'antd-mask-input'
import {
	changePassword,
	ChangePasswordAnswer,
	getChangePasswordToken,
	GetChangePasswordTokenAnswer
} from '../../../api/security'
import { useHistory } from 'react-router'
import { signOut } from '../../../api/signIn'

interface IProps {
	open: boolean
	close: () => void
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ChangePasswordModal = (props: IProps): React.ReactElement => {
	const history = useHistory()

	const [loading, setIsLoading] = useState(false)
	const [error, setError] = useState('')
	const [currentStep, setCurrentStep] = useState(0)
	const [token, setToken] = useState('')

	const [mfaNeeded, setMfaNeeded] = useState(false)
	const [form] = Form.useForm<{ oldPassword: string; mfaCode: string }>()
	const [changePasswordForm] = Form.useForm<{ newPassword: string }>()

	async function getToken() {
		const fieldValues = form.getFieldsValue()
		const result = await getChangePasswordToken(
			fieldValues.oldPassword,
			fieldValues.mfaCode ? fieldValues.mfaCode.replaceAll(' ', '') : ''
		)

		if (result.success) {
			let nextStep = currentStep
			nextStep++

			setToken(result.token)
			setCurrentStep(nextStep)
			setError('')
			form.resetFields()
		} else {
			switch (result.message) {
				case GetChangePasswordTokenAnswer.MFA_REQUIRED: {
					setMfaNeeded(true)
					break
				}
				case GetChangePasswordTokenAnswer.BAD_MFA: {
					setError('Неверный код двухфакторной авторизации!')
					break
				}
				case GetChangePasswordTokenAnswer.BAD_PASSWORD: {
					setError('Неверный старый пароль!')
					break
				}
				case GetChangePasswordTokenAnswer.NOT_AUTHORIZED: {
					await signOut()

					history.push('/auth')
					break
				}
			}
		}

		setIsLoading(false)
	}

	async function changePass() {
		const result = await changePassword(token, changePasswordForm.getFieldsValue().newPassword)

		if (result.success) {
			void message.success('Пароль успешно изменён!')
			setToken('')
			changePasswordForm.resetFields()
			setError('')
			setCurrentStep(0)
			setMfaNeeded(false)

			props.close()
		} else {
			switch (result.message) {
				case ChangePasswordAnswer.BAD_TOKEN: {
					setCurrentStep(0)
					break
				}
				case ChangePasswordAnswer.BAD_PASSWORD: {
					setError('Пароль не соответствует критериям.')
					break
				}
				case ChangePasswordAnswer.NOT_AUTHORIZED: {
					await signOut()

					history.push('/auth')
					break
				}
			}
		}
		setIsLoading(false)
	}

	return (
		<Modal
			title="Изменение пароля"
			closable={!loading}
			visible={props.open}
			onCancel={() => {
				if (!loading) {
					props.close()
				}
			}}
			cancelText="Закрыть"
			cancelButtonProps={{ disabled: loading }}

			okText={currentStep === 0 ? 'Далее' : 'Изменить пароль'}
			okButtonProps={{
				loading,
				onClick: currentStep === 0 ? () => {
					setIsLoading(true)
					void getToken()
				} : () => {
					setIsLoading(true)
					void changePass()
				}
			}}
		>
			<ContentSteps loading={loading} error={Boolean(error)} currentStepId={currentStep}>
				<ContentStep
					title="Верификация"
					icon={<span className={styles['step-icon']}></span>}
				>
					<Typography.Title level={4}>
						Привет!
					</Typography.Title>

					<Typography>
						Чтобы изменить пароль, нам необходимо убедиться, что вашим
						аккаунтом не завладели злоумышленники, поэтому вам необходимо ввести
						ваш старый пароль и код двухфакторной авторизации (при надобности)
						в поля ниже.
					</Typography>

					{error ? (
						<Typography style={{ color: 'red', marginTop: '5%' }}>
							{error}
						</Typography>
					) : <></>}

					<Form style={{ marginTop: '5%' }} layout="vertical" form={form}>
						<Form.Item required label="Старый пароль" name="oldPassword">
							<Input.Password disabled={loading}/>
						</Form.Item>

						{mfaNeeded ? (
							<Form.Item
								className={modalStyles['mfa-code-field']}
								required
								label="Код двухфакторной авторизации"
								name="mfaCode"
							>
								<MaskedInput mask="111 111" disabled={loading}/>
							</Form.Item>
						) : <></>}
					</Form>
				</ContentStep>
				<ContentStep
					title="Смена пароля"
					icon={<EditOutlined/>}
				>
					{error ? (
						<Typography style={{ color: 'red', marginTop: '5%' }}>
							{error}
						</Typography>
					) : <></>}

					<Form style={{ marginTop: '5%' }} layout="vertical" form={changePasswordForm}>
						<Form.Item required label="Новый пароль" name="newPassword">
							<Input.Password disabled={loading}/>
						</Form.Item>
					</Form>
				</ContentStep>
			</ContentSteps>
		</Modal>
	)
}
